import React from 'react';
import moment from 'moment';
import { Avatar, Tooltip } from 'antd';
import uuid from 'uuid';
import { useHistory, useLocation } from 'react-router-dom';
import cnnaid from '../../../../../../../../assets/icons/transactions/cnnaid.png';
import cnnaip from '../../../../../../../../assets/icons/transactions/cnnaip.png';
import cnnafingerprint from '../../../../../../../../assets/icons/transactions/cnnafingerprint.png';
import { ReactComponent as GlobePhoneIcon } from '../../../../../../../../assets/icons/transactions/globephone.svg';
import ImpressionTimeline from './ImpressionsTimeLine';

// Global Styling
import {
  TableColumnTitle,
  PrimaryTableRowText,
} from '../../../../../../../shared/globalStyling/styledText';
import { formatCurrency2SigFig } from '../../../../../../../../core/utils/campaigns';
import SimpleTable from '../../../../../../../shared/SimpleTable';

const TransactionsTable = ({
  transactionData = [],
  transactionsPagination = { total: 0, hasNextPage: false },
  getImpressions,
}) => {
  const history = useHistory();
  const location = useLocation();

  // Read pagination and sort from URL params
  const queryParams = new URLSearchParams(location.search);
  const currentPage = parseInt(queryParams.get('page')) || 1;
  const pageSize = parseInt(queryParams.get('pageSize')) || 10;
  const SORTABLE_FIELDS = ['orderId', 'lastImpression', 'totalImpressions', 'transactionDate', 'revenue'];
  const sortFieldParam = queryParams.get('sortField');
  const sortOrderParam = queryParams.get('sortOrder');
  const activeSortField = SORTABLE_FIELDS.includes(sortFieldParam) ? sortFieldParam : null;
  const activeSortOrder =
    activeSortField && (sortOrderParam === 'ascend' || sortOrderParam === 'descend')
      ? sortOrderParam
      : null;
  const sortOrderFor = (field) => (activeSortField === field ? activeSortOrder : null);

  const handleTableChange = (paginationObject, _filtersObject, sorterObject) => {
    const params = new URLSearchParams(location.search);

    const nextPageSize = paginationObject.pageSize;
    const nextPage =
      nextPageSize !== pageSize ? 1 : paginationObject.current;
    params.set('page', String(nextPage));
    params.set('pageSize', String(nextPageSize));

    const nextField = sorterObject && sorterObject.order ? sorterObject.field : null;
    const nextOrder = sorterObject && sorterObject.order ? sorterObject.order : null;
    if (nextField && nextOrder) {
      params.set('sortField', nextField);
      params.set('sortOrder', nextOrder);
    } else {
      params.delete('sortField');
      params.delete('sortOrder');
    }

    // Sort changed (without an explicit page change) - reset to page 1
    const sortChanged =
      nextField !== activeSortField || nextOrder !== activeSortOrder;
    if (sortChanged && paginationObject.current === currentPage && nextPageSize === pageSize) {
      params.set('page', '1');
    }

    history.push({ search: params.toString() });
  };

  const columns = [
    {
      title: <TableColumnTitle text={'Order'} />,
      dataIndex: 'orderId',
      key: 'orderId',
      align: 'left',
      width: '13%',
      sorter: true,
      sortOrder: sortOrderFor('orderId'),
      render: (orderId) => <PrimaryTableRowText text={orderId || 'N/A'} />,
    },
    {
      title: <TableColumnTitle text={'Last Impression'} />,
      dataIndex: 'lastImpression',
      key: 'lastImpression',
      align: 'left',
      width: '20%',
      sorter: true,
      sortOrder: sortOrderFor('lastImpression'),
      render: (lastImpression) => (
        <PrimaryTableRowText
          text={lastImpression ? moment(lastImpression).format('MM/DD/YY HH:mm') : ''}
        />
      ),
    },
    {
      title: <TableColumnTitle text={'Impressions'} />,
      dataIndex: 'totalImpressions',
      key: 'impressions',
      align: 'left',
      width: '17%',
      sorter: true,
      sortOrder: sortOrderFor('totalImpressions'),
      render: (totalImpressions) => <PrimaryTableRowText text={totalImpressions || 0} />,
    },
    {
      title: <TableColumnTitle text={'Ordered on'} />,
      dataIndex: 'transactionDate',
      key: 'transactionDate',
      align: 'left',
      width: '15%',
      sorter: true,
      sortOrder: sortOrderFor('transactionDate'),
      render: (transactionDate) => (
        <PrimaryTableRowText
          text={transactionDate ? moment(transactionDate).format('MM/DD/YY HH:mm') : ''}
        />
      ),
    },
    {
      title: <TableColumnTitle text={'Total'} />,
      dataIndex: 'revenue',
      key: 'revenue',
      align: 'left',
      width: '12%',
      sorter: true,
      sortOrder: sortOrderFor('revenue'),
      render: (revenue) => (
        <PrimaryTableRowText
          text={
            revenue && !isNaN(revenue) ? formatCurrency2SigFig(revenue) : formatCurrency2SigFig(0)
          }
        />
      ),
    },
    {
      title: <TableColumnTitle text={''} />,
      dataIndex: 'attrMethod',
      key: 'attrMethod',
      align: 'left',
      width: '8%',
      render: (attrMethod) => {
        let attributionIcon = cnnaip;
        let tip = '';

        if (attrMethod === 'COOKIE') {
          attributionIcon = cnnaid;
          tip = 'Cookie Match - Deterministic match using browser based cookie / uuid match.';
        } else if (attrMethod === 'FINGERPRINT') {
          attributionIcon = cnnafingerprint;
          tip =
            'User Match - Probabilistic match using user fingerprint. (User fingerprint - The combination of user agent and IP address to produce a unique hash)';
        } else if (attrMethod === 'DEVICE_FINGERPRINT') {
          tip =
            'Device Match - Probabilistic match using user device model information (Device model, OS, OS version ) combined with IP address';
        } else if (attrMethod === 'IP') {
          tip = 'IP Match - Probabilistic match using IP address.';
          attributionIcon = cnnaip;
        }

        const attributionIcon_element = () => {
          if (attrMethod === 'DEVICE_FINGERPRINT') {
            return (
              <span role="img" className="anticon" style={{ verticalAlign: '-5px' }}>
                <GlobePhoneIcon
                  height="35"
                  width="35"
                  style={{
                    opacity: '1.0',
                  }}
                />
              </span>
            );
          } else {
            return <Avatar src={attributionIcon} />;
          }
        };

        return <Tooltip title={tip}>{attributionIcon_element()}</Tooltip>;
      },
    },
  ];

  return (
    <SimpleTable
      footer={transactionData}
      bordered={false}
      columns={columns}
      data={transactionData.map((transaction) => ({
        ...transaction,
        key: `${transaction.id}${uuid.v4()}`,
      }))}
      scroll={{ x: 850, y: 400 }}
      onChange={handleTableChange}
      pagination={{
        pageSize: pageSize,
        total: transactionsPagination.total,
        current: currentPage,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50', '100'],
      }}
      expandable={{
        expandedRowRender: (record) => {
          return <ImpressionTimeline transactionId={record.id} getImpressions={getImpressions} />;
        },
        rowExpandable: (record) => record.totalImpressions > 0,
      }}
      rowClassName={(record) => {
        return record && record.totalImpressions > 0 ? '' : 'hide-expand';
      }}
    />
  );
};

export default TransactionsTable;
